<template>
    <li
        class="node-tree nav-item cursor-pointer"
        :class="{ 'has-treeview': isGroup, 'd-none': !filtered }"
        :data-id="node.id"
    >
        <template v-if="isGroup">
            <div
                v-if="!isRoot || (isRoot && showRoot)"
                @click.prevent="toggleEvent('selected', node, $event)"
                class="nav-link group d-flex align-items-center"
                :class="{ selected: selected && activeSelect }"
                :aria-expanded="!collapsed"
            >
                <div
                    v-if="!loading"
                    class="nav-icon"
                    :style="{ 'background-color': node.icon_bg_color ? node.icon_bg_color : '' }"
                >
                    <i
                        class=""
                        :class="node.icon ? node.icon : 'fas fa-th'"
                    ></i>
                </div>

                <i
                    v-if="showCheckbox && !loading"
                    @click.stop="toggleEvent('checked', node, $event)"
                    :class="[intermediateChecked ? 'fa-square-minus' : checked ? 'fa-check-square' : 'fa-square']"
                    class="nav-icon-onoff far"
                    data-toggle="tooltip-city"
                    title="Toggle group visibility"
                    data-placement="top"
                >
                </i>
                <span class="nav-link-text">
                    {{ node.name }}

                    <span
                        v-if="hasChildren"
                        @click.stop="toggleEvent('collapsed', node, $event)"
                        class="right"
                        data-toggle="tooltip-city"
                        title="Toggle Group Collapse/Open"
                        data-placement="top"
                    >
                        <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="fas"
                            data-icon="angle-down"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 256 512"
                            class="rotate-icon small svg-inline--fa fa-angle-down"
                        >
                            <path
                                fill="currentColor"
                                d="M119.5 326.9L3.5 209.1c-4.7-4.7-4.7-12.3 0-17l7.1-7.1c4.7-4.7 12.3-4.7 17 0L128 287.3l100.4-102.2c4.7-4.7 12.3-4.7 17 0l7.1 7.1c4.7 4.7 4.7 12.3 0 17L136.5 327c-4.7 4.6-12.3 4.6-17-.1z"
                                class=""
                            ></path>
                        </svg>
                    </span>
                </span>
            </div>
        </template>
        <template v-else>
            <div
                class="nav-link layer"
                :class="{ active: checked, selected: selected && activeSelect }"
                @click.prevent="toggleEvent('selected', node, $event)"
            >
                <i
                    v-if="showCheckbox && !loading"
                    @click.stop="toggleEvent('checked', node, $event)"
                    class="nav-icon-sm far"
                    :class="[checked ? 'fa-eye' : 'fa-eye-slash']"
                    data-toggle="tooltip-city"
                    title="Toggle layer visibility"
                    data-placement="top"
                >
                </i>

                <img
                    v-if="iconLayer"
                    :src="iconLayer"
                    loading="lazy"
                    class="img-icon-layer"
                    alt="..."
                />

                <span class="nav-link-text">
                    {{ node.name }}

                    <span
                        v-if="enableLegendLayer"
                        @click.stop="onToggleLegend(node, $event)"
                        data-toggle="tooltip-city"
                        title="Display legend and tools"
                        data-placement="top"
                        class="right"
                    >
                        <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="fas"
                            data-icon="ellipsis-h"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                            class="nav-icon svg-inline--fa fa-ellipsis-h"
                        >
                            <path
                                fill="currentColor"
                                d="M328 256c0 39.8-32.2 72-72 72s-72-32.2-72-72 32.2-72 72-72 72 32.2 72 72zm104-72c-39.8 0-72 32.2-72 72s32.2 72 72 72 72-32.2 72-72-32.2-72-72-72zm-352 0c-39.8 0-72 32.2-72 72s32.2 72 72 72 72-32.2 72-72-32.2-72-72-72z"
                                class=""
                            ></path>
                        </svg>
                    </span>
                </span>
            </div>

            <div
                v-if="checked || !collapsed"
                class="row align-items-center no-gutters"
            >
                <div class="col">
                    <OpacitySlider
                        :disabled="!checked"
                        class="layer-opacity-slider"
                        :id="`layer-slider-${node.id}`"
                        :min="0"
                        :max="1"
                        :interval="0.1"
                        :contained="true"
                        v-model="opacity"
                        tooltip="always"
                        :tooltipStyles="{ right: 10 }"
                        tooltipPlacement="right"
                    >
                        <template v-slot:dot="{ value, focus }">
                            <div
                                class="vue-slider-dot-handle"
                                :class="['custom-dot', { focus }]"
                            ></div>
                        </template>
                        <template v-slot:tooltip="{ value, focus }">
                            <div class="layer-opacity-slider-tooltip-inner opacity-slider-tooltip-inner-right">
                                <span
                                    class="layer-opacity-slider-tooltip-text"
                                    :class="[{ focus }]"
                                    >{{ value * 100 }}%</span
                                >
                            </div>
                        </template>
                    </OpacitySlider>
                </div>
                <div class="col-auto node-actions">
                    <button
                        v-for="(action, idx) in filteredActions"
                        :key="`node-action-${idx}`"
                        :title="action.title"
                        :disabled="!checked"
                        @click.prevent="callNodeAction(action, $event)"
                        type="button"
                        class="btn node-action"
                        data-toggle="tooltip-city"
                    >
                        <i
                            v-if="action.icon"
                            class=""
                            :class="action.icon"
                        ></i>
                        <span
                            v-else-if="action.image"
                            v-html="action.image"
                        ></span>
                    </button>
                </div>
            </div>
        </template>
        <collapse-transition>
            <ul
                v-if="hasChildren || enableLegendLayer"
                class="nav nav-treeview cursor-default"
                :class="{ 'pl-0': (isRoot && !showRoot) || enableLegendLayer }"
                v-show="!collapsed"
                :id="`sb-item-${node.id}`"
            >
                <NodeTreeLayers
                    v-for="child in node.children"
                    :ref="`node-tree-${child.id}`"
                    :key="`node-tree-${child.id}`"
                    :node="child"
                    :parentNode="node"
                    :depth="depth + 1"
                    :map="map"
                    :actions="actions"
                    :showRoot="showRoot"
                    :activeSelect="activeSelect"
                    :minimumIconSize="minimumIconSize"
                    @emitNodeAction="emitNodeAction"
                    @emitNodeChecked="emitNodeChecked"
                    @emitNodeCollapsed="emitNodeCollapsed"
                    @emitNodeSelected="emitNodeSelected"
                    @emitNodeDeleted="emitNodeDeleted"
                    @emitNodeAdded="emitNodeAdded"
                />

                <li
                    v-if="enableLegendLayer"
                    class="nav-item legend m-2"
                >
                    <LayerLegend
                        :show="!collapsed"
                        :key="`sb-item-legend-${node.id}`"
                        :url="legendUrl"
                        @show="setLegendUrl"
                    />
                </li>
            </ul>
        </collapse-transition>
    </li>
</template>

<script>
import NodeLayerMixin from "../NodeLayerMixin";
import "vue-slider-component/theme/antd.css";
import { isFunction } from "@config/utils";

export default {
    name: "NodeTreeLayers",
    mixins: [NodeLayerMixin],
    components: {
        OpacitySlider: () => import(/* webpackChunkName: "vue-slider-component" */ "vue-slider-component"),
        CollapseTransition: () => import(/* webpackChunkName: "collapse-transition" */ "@/app/shared/components/common/CollapseTransition"),
        LayerLegend: () => import(/* webpackChunkName: "layer-legend-component" */ "./LayerLegendComponent")
    },
    props: {
        rootId: {
            type: Number,
            default: 0
        },
        node: {
            type: Object,
            default: () => {}
        },
        depth: {
            type: Number,
            default: 1
        },
        parentNode: {
            type: Object
        },
        showRoot: {
            type: Boolean,
            default: false
        },
        showCheckbox: {
            type: Boolean,
            default: true
        },
        activeSelect: {
            type: Boolean,
            default: true
        },
        actions: {
            type: Array,
            default: () => {
                return [
                    {
                        id: "zoom_to",
                        icon: "fas fa-search-plus"
                    },
                    {
                        id: "metadata",
                        icon: "fas fa-info"
                    },
                    {
                        id: "attribute_table",
                        icon: "fas fa-table"
                    }
                ];
            }
        },
        map: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            checked: false,
            collapsed: false,
            selected: false,
            filtered: true,
            opacity: 1,
            eventClick: {
                delay: 300,
                clicks: 0,
                timer: null
            },
            legendUrl: null,
            intermediateChecked: false
        };
    },
    mounted() {
        const defaultState = { loaded: false, loading: false, checked: false, collapsed: true, selected: false, filtered: true, opacity: 1, intermediateChecked: false };
        this.node.state = { ...defaultState, ...this.node.state };

        this.loading = this.node.state.loading;
        this.collapsed = this.node.state.collapsed;
        this.selected = this.node.state.selected;
        this.checked = this.node.state.checked;
        this.filtered = this.node.state.filtered;
        this.opacity = this.node.state.opacity;
        this.intermediateChecked = this.node.state.intermediateChecked;
        // if(this.node.collapse==undefined){
        //     this.$set(this.node, 'collapse', this.isRoot ? false : true);
        // }
        // this.collapsed = this.isGroup ? this.node.collapse : true;

        // if(this.node.selected==undefined){
        //     this.$set(this.node, 'selected', false);
        // }
        // this.selected = this.node.selected;

        // if(this.node.visible==undefined){
        //     this.$set(this.node, 'visible', false);
        // }
        // this.checked = this.node.visible;

        // if(this.node.filtered==undefined){
        //     this.$set(this.node, 'filtered', true);
        // }

        // if(this.node.opacity==undefined){
        //     this.$set(this.node, 'opacity', 1);
        // }
        // this.opacity = this.node.opacity;

        // if(this.node.depth==undefined){
        //     this.$set(this.node, 'depth', this.depth);
        // }

        // FORCE TO ADD ALL INITIAL LAYERS TO MAP
        if (this.isChecked) {
            this.applyStateOnMap(this.node);
        }
        this.updateCheckedFromChildren();
    },
    computed: {
        isChecked() {
            return this.checked;
        },
        expanded() {
            return !this.collapsed;
        },
        isRoot() {
            return this.node.root === true || (this.node && this.node.id === this.rootId);
        },
        hasChildren() {
            return this.node.children && this.node.children.length;
        },
        isGroup() {
            // return this.node.children && !this.isNodeLayer(this.node);
            return this.hasChildren || this.node.behavior == this.BEHAVIOR_GROUP;
        },
        iconLayer() {
            return this.node.icon_layer;
        },
        filteredActions() {
            return this.actions.filter(action => !!(isFunction(action.active) ? action.active(this.node) : action.active));
        }
    },
    methods: {
        toggleEvent(eventType, node, evt) {
            const eventTypeCapitalized = eventType.charAt(0).toUpperCase() + eventType.slice(1);
            const fnName = `toggle${eventTypeCapitalized}`;
            this[fnName](node, evt);
        },
        toggleCollapsed(node, evt) {
            this.collapsed = !this.collapsed;
            this.node.state.collapsed = this.collapsed;
            this.$nextTick(() => {
                this.emitNodeCollapsed(node, this.collapsed);
            });
        },
        toggleSelected(node, evt) {
            let config = this.eventClick;
            config.clicks++;
            if (config.clicks === 1) {
                config.timer = setTimeout(() => {
                    config.clicks = 0;

                    let selectable = node.selectable === undefined || node.selectable;
                    if (!selectable) return;
                    this.selected = !this.selected;
                    this.node.state.selected = this.selected;
                    this.emitNodeSelected(node, evt);
                }, config.delay);
            } else if (config.clicks === 2) {
                clearTimeout(config.timer);
                config.clicks = 0;
                this.toggleCollapsed(node, evt);
            }
        },
        toggleChecked(node, evt) {
            this.checked = !this.checked;
            this.node.state.checked = this.checked;
            this.$nextTick(() => {
                this.callNodesChecked(this.checked);
                this.emitNodeChecked(node);

                Promise.resolve().then(() => {
                    this.updateCheckedFromChildren();
                });
            });
        },
        emitNodeSelected(nodeSelected) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeSelected", nodeSelected);
        },
        emitNodeCollapsed(node, state) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeCollapsed", node, state);
        },
        emitNodeChecked(nodeChecked) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeChecked", nodeChecked);
        },
        emitNodeDeleted(nodeDeleted) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeDeleted", nodeDeleted);
        },
        emitNodeAdded(nodeAdded) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeAdded", nodeAdded);
        },
        emitNodeAction(action, node, evt) {
            // redirect the event toward the Tree component
            this.$emit("emitNodeAction", action, node, evt);
        },
        recCallNodes(state, event, nodes, callback) {
            const _this = this;
            nodes.forEach(node => {
                // if (!node.state) node.state = { checked: false, expanded: false, selected: false }
                if (event) {
                    node[event] = state;
                }
                if (callback) {
                    callback(node);
                }
                if (node.children) {
                    _this.recCallNodes(state, event, node.children, callback);
                }
            });
        },
        callNodesChecked(state) {
            this.checked = state;
            this.intermediateChecked=false;
            //this.node.intermediateChecked=false;
            let vueChildren = [];
            this.$children.map(c => {
                if (c.$options.name == "CollapseTransition") {
                    vueChildren = [...vueChildren, ...c.$children];
                } else {
                    vueChildren.push(c);
                }
            });
            for (let i = 0; i < vueChildren.length; i++) {
                if (typeof vueChildren[i].callNodesChecked === "function") {
                    vueChildren[i].callNodesChecked(state);
                }
            }
            if (vueChildren.length === 0 && this.node.children && this.node.children.length > 0) {
                this.recCallNodes(state, "checked", this.node.children);
            }
        },
        updateCheckedFromChildren() {
            let vueChildren = [];

            // Obter filhos reais (ignorando collapse-transition)

            this.$children.forEach(c => {
                if (c.$options.name === "CollapseTransition") {
                    const realChildren = c.$children.filter(child => child.$options.name === "NodeTreeLayers");

                    vueChildren.push(...realChildren);
                } else if (c.$options.name === "NodeTreeLayers") {
                    vueChildren.push(c);
                }
            });

            if (vueChildren.length > 0) {
                const children = vueChildren.filter(c => c.node);

                const allChecked = children.every(c => c.checked === true);
                const noneChecked = children.every(c => c.checked === false);
                const someIntermediate = children.some(c => c.intermediateChecked === true);

                this.intermediateChecked = (!allChecked && !noneChecked) || someIntermediate;
                if (this.intermediateChecked) {
                    this.checked = false;
                } else if (allChecked) this.checked = true;
                else if (noneChecked) this.checked = false;
   
            }

            // Subir ao pai mesmo que este nó seja folha
            const realParent = this.getRealParent(this);
            if (realParent && typeof realParent.updateCheckedFromChildren === "function") {
                realParent.updateCheckedFromChildren();
            }
        },

        getRealParent(vm) {
            let parent = vm.$parent;
            while (parent && parent.$options.name === "CollapseTransition") {
                parent = parent.$parent;
            }
            return parent;
        },
        callNodesDeselect() {
            this.selected = false;
            this.node.selected = this.selected;
            for (let i = 0; i < this.$children.length; i++) {
                if (typeof this.$children[i].callNodesDeselect === "function") {
                    this.$children[i].callNodesDeselect();
                }
            }
            if (this.$children.length === 0 && this.node.children && this.node.children.length > 0) {
                this.recCallNodes(false, "selected", this.node.children);
            }
        },
        callSpecificChild(arrIds, fname, args) {
            let vueChildren = [];
            this.$children.map(c => {
                if (c.$options.name == "CollapseTransition") {
                    vueChildren = [...vueChildren, ...c.$children];
                } else {
                    vueChildren.push(c);
                }
            });
            for (let i = 0; i < vueChildren.length; i++) {
                let currentNodeId = vueChildren[i].$props?.node?.id;
                if (currentNodeId && arrIds.find(x => x == currentNodeId)) {
                    if (typeof vueChildren[i][fname] === "function") {
                        vueChildren[i][fname](args);
                    }
                    return false;
                }
            }
        },
        callNodeChecked(args) {
            const arrIds = args.arrIds;
            const value = args.value;
            if (arrIds[arrIds.length - 1] == this.node.id) {
                this.checked = value;
                this.callNodesChecked(this.checked);
                this.emitNodeChecked(this.node);
            } else {
                this.collapsed = false;
                this.$nextTick(() => {
                    this.callSpecificChild(arrIds, "callNodeChecked", args);
                });
            }
        },
        callNodeSelected(args) {
            const arrIds = args.arrIds;
            const value = args.value;
            if (arrIds[arrIds.length - 1] == this.node.id) {
                this.selected = value;
            } else {
                // this.collapsed = true
                this.$nextTick(() => {
                    this.callSpecificChild(arrIds, "callNodeSelected", args);
                });
            }
        },
        callNodeCollapsed(args) {
            const arrIds = args.arrIds;
            const value = args.value;
            if (this.isRoot && this.showRoot) return;
            // if ((this.isRoot && this.showRoot || !this.isRoot) && value === false && this.collapsed === false) return
            if (arrIds[arrIds.length - 1] != this.node.id) {
                if (value === true) {
                    this.collapsed = true;
                    this.$nextTick(() => {
                        this.callSpecificChild(arrIds, "callNodeCollapsed", args);
                    });
                } else {
                    this.callSpecificChild(arrIds, "callNodeCollapsed", args);
                }
            } else {
                this.collapsed = value;
            }
        },
        callNodeDeleted(args) {
            const arrIds = args.arrIds;
            if (arrIds[arrIds.length - 1] != this.node.id) {
                this.callSpecificChild(arrIds, "callNodeDeleted", args);
            } else {
                this.removeLayerFromMap(this.node);
                if (this.node.children && this.node.children.length > 0) {
                    this.recCallNodes(null, null, this.node.children, node => {
                        this.removeLayerFromMap(node);
                        this.emitNodeDeleted(node);
                    });
                }
                this.emitNodeDeleted(this.node);
                this.$nextTick(() => {
                    this.updateCheckedFromChildren();
                });
            }
        },
        callNodeAdded(args) {
            const arrIds = args.arrIds;
            if (arrIds[arrIds.length - 1] == this.node.id) {
                // this.applyStateOnMap(this.node);
                this.emitNodeAdded(this.node);

                this.$nextTick(() => {
                    this.updateCheckedFromChildren();
                });
            } else {
                this.$nextTick(() => {
                    this.callSpecificChild(arrIds, "callNodeAdded", args);
                });
            }
        },
        callNodeAction(action, evt) {
            this.emitNodeAction(action, this.node, evt);
        },
        onToggleLegend(node, evt) {
            this.toggleEvent('collapsed', node, evt);
        },
        setLegendUrl() {
            this.legendUrl = this.getLegendUrl(this.node);
        }
    },
    watch: {
        checked(value) {
            // console.log('watch checked:', this.node.name);
            this.node.state.checked = value;
            this.applyStateOnMap(this.node);
            // this.activeTooltip();
        },
        intermediateChecked(value) {
            // console.log('watch checked:', this.node.name);
            this.node.state.intermediateChecked = value;

            this.applyStateOnMap(this.node);
            // this.activeTooltip();
        },
        collapsed(value) {
            this.node.state.collapsed = value;
        },
        selected(value) {
            // console.log('watch selected:', this.node.name);
            this.node.state.selected = value;
        },
        filtered(value) {
            // console.log('watch filtered:', this.node.name);
            this.node.state.filtered = filtered;
        },
        opacity(value) {
            this.node.state.opacity = value;
            this.applyStateOnMap(this.node);
        }
    }
};
</script>

<style lang="scss" scoped>
.node-actions {
    .btn {
        margin-left: 0.2rem;
    }
}
.img-icon-layer {
    padding: 0 0.2rem;
    background-color: #fff;
    border-radius: 0.25rem;
    max-height: 16px;
    max-width: 100%;
    height: auto;
}
</style>
